import { useState, type FormEvent } from "react";
import { AlertTriangle, ArrowRight, LockKeyhole, Mail, ShieldCheck, UserRound } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export function AuthPage() {
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState<"sign-in" | "sign-up">("sign-in");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    try {
      if (mode === "sign-in") {
        await signIn(email.trim(), password);
      } else {
        const signedIn = await signUp(fullName, email.trim(), password);
        if (!signedIn) {
          setMessage("Check your email to confirm the account, then return here to sign in.");
        }
      }
    } catch (submissionError) {
      setError(
        submissionError instanceof Error
          ? submissionError.message
          : "The account request could not be completed.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-page">
      <section className="auth-story">
        <div className="auth-brand">
          <span className="brand-mark">DS</span>
          <div>
            <strong>Design Safety MY</strong>
            <small>by OSHKKP</small>
          </div>
        </div>
        <div>
          <span className="eyebrow">Secure project assurance</span>
          <h1>Design decisions with a defensible evidence trail.</h1>
          <p>
            Organise Malaysian CDM project facts, duty triggers, design-risk decisions and
            controlled evidence in one reviewable workspace.
          </p>
        </div>
        <ul className="auth-benefits">
          <li><ShieldCheck size={19} /> Organisation-scoped access</li>
          <li><LockKeyhole size={19} /> Row Level Security</li>
          <li><AlertTriangle size={19} /> Source-linked accountable decisions</li>
        </ul>
      </section>

      <section className="auth-panel">
        <div className="auth-card">
          <span className="eyebrow">{mode === "sign-in" ? "Welcome back" : "Secure workspace"}</span>
          <h2>{mode === "sign-in" ? "Sign in to your workspace" : "Create your account"}</h2>
          <p>
            {mode === "sign-in"
              ? "Use the email and password registered for Design Safety MY."
              : "Start with your own secure organisation workspace."}
          </p>

          <div className="auth-tabs" role="tablist" aria-label="Account action">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "sign-in"}
              className={mode === "sign-in" ? "active" : ""}
              onClick={() => {
                setMode("sign-in");
                setError("");
                setMessage("");
              }}
            >
              Sign in
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "sign-up"}
              className={mode === "sign-up" ? "active" : ""}
              onClick={() => {
                setMode("sign-up");
                setError("");
                setMessage("");
              }}
            >
              Create account
            </button>
          </div>

          <form onSubmit={submit} className="auth-form">
            {mode === "sign-up" && (
              <label>
                <span>Full name</span>
                <div className="auth-input">
                  <UserRound size={18} />
                  <input
                    value={fullName}
                    onChange={(event) => setFullName(event.target.value)}
                    autoComplete="name"
                    required
                  />
                </div>
              </label>
            )}
            <label>
              <span>Email address</span>
              <div className="auth-input">
                <Mail size={18} />
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                  required
                />
              </div>
            </label>
            <label>
              <span>Password</span>
              <div className="auth-input">
                <LockKeyhole size={18} />
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete={mode === "sign-in" ? "current-password" : "new-password"}
                  minLength={8}
                  required
                />
              </div>
              {mode === "sign-up" && <small>Use at least eight characters.</small>}
            </label>

            {error && <div className="auth-message auth-error" role="alert">{error}</div>}
            {message && <div className="auth-message auth-success" role="status">{message}</div>}

            <button className="button button-primary auth-submit" type="submit" disabled={busy}>
              {busy ? "Please wait…" : mode === "sign-in" ? "Sign in" : "Create secure account"}
              {!busy && <ArrowRight size={17} />}
            </button>
          </form>

          <p className="auth-limitation">
            Decisions remain attributable to named project dutyholders. Official sources and
            evidence status stay visible throughout the assurance trail.
          </p>
        </div>
      </section>
    </main>
  );
}
